/* eslint-disable no-inner-declarations, no-undef, no-restricted-globals, react/no-danger, no-unused-vars*/
import { useMutation, useQuery } from '@apollo/react-hooks';
import React, { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import Select from 'react-select';
import { UserContext } from '../context/UserContext';
import { GET_ENV_DATA, UPDATE_ORG_INTEGRATION, GET_CURRENT_ORGANIZATION } from '../graphql/queries';
import colors from '../styles/colors';
import Button from './Button';
import Card from './Card';
import Icon from './Icon';
import { IconEnum } from './Icons';
import Input, { TextAreaInput } from './Input';
import { getErrorMessage } from '../util/ErrorUtil';

const Form = styled.form`
  width: 80%;
  position: relative;
  @media (max-width: 991px) {
    width: 100%;
  }
`;

export const DropdownHeader = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 30px 0px 10px 0px;
`;

export const DropdownContainer = styled.div`
  font-size: 1.3rem;
  width: 100%;
`;
const InputContainer = styled.div`
  display: flex;
  justify-content: ${(props) => props.justify || 'space-between'};
  flex-direction: ${(props) => props.direction || null};
  width: 100%;
  margin: ${(props) => props.margin || '0'};
  border: ${(props) => props.border || null};
  padding: ${(props) => props.padding || null};
`;

export const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: ${(props) => props.margin || '0'};
  align-items: center;
  justify-content: space-evenly;
  @media (max-width: 991px) {
    justify-content: space-evenly;
  }
  @media (max-width: 480px) {
    margin: ${(props) => props.marginXs || '0'};
  }
`;

export const ScriptDiv = styled.div`
  display: flex;
  width: 100%;
  border-radius: 7px;
  border: 1px solid ${colors.grey5};
  overflow: auto;
  word-break: break-word;
`;

export const Label = styled.label`
  color: ${colors.grey3};
  display: flex;
  flex: 1;
  font-size: 13px;
  margin-top: ${(props) => props.marginTop || '0'};
  margin-bottom: ${(props) => props.marginBottom || '0'};
  margin-left: 10px;

  @media screen and (max-width: 991px) {
    margin-right: 0px;
  }
`;

const Text = styled.div`
  padding: 10px;
  font-size: 13px;
  color: black;
  white-space: pre-line;
`;

const LabelDiv = styled.div`
  display: flex;
  width: max-content;
`;

const INTEGRATION_TYPES = Object.freeze({
  BUTTON: 'BUTTON',
  AUTO_POP_UP: 'AUTO_POP_UP',
});

const getNormalizedIntegrationType = (type) => {
  if (type === INTEGRATION_TYPES.BUTTON || type === INTEGRATION_TYPES.AUTO_POP_UP) {
    return type;
  }
  return INTEGRATION_TYPES.AUTO_POP_UP;
};

function getEmbedHTMLForButton(
  organizationId,
  text,
  buttonColor,
  color,
  integrationType,
) {
  return `
    <a href="javascript:void(0)" style="text-decoration:none;">
      <img src="/favicon.ico"  style="display:none;"> 
      <div style="
        background-color:${buttonColor}; 
        color:${color}; 
        font-family: sans-serif;
        border-radius: 100px; 
        height:50px; 
        width:100%; 
        display:inline-flex; 
        align-items:center; 
        justify-content:center; 
        font-weight: 600;
        font-size: 14px;
      "
      onClick="window.parent.CTO.open('${organizationId}')">${text}</div>
    </a>
  `;
}
const DefaultColors = Object.freeze({
  backgroundColor: '#0f233c',
  buttonTextColor: '#FFFFFF',
  buttonColor: colors.seaGreen,
});

const DefaultText = {
  headline: 'Become a VIP',
  introductoryText:
    'Be the first to know about gift card giveaways, specials, exclusive events, and more!',
  succesText:
    "Thank you for enjoying your experience at {OrgName} and for joining our VIP list.  We'll keep you informed of our latest specials, events, and giveaways, and we promise to keep the texts enjoyable and to-the-point.  We look forward to seeing you at {OrgName} again soon!",
};

const TextLength = {
  max: {
    headline: 15,
    introductoryText: 300,
  },
};

const getMaxCharErrorMessage = (limit) => {
  return `Only ${limit} characters are allowed`;
};

const IntegrationSettings = () => {
  const { user, setUser } = useContext(UserContext);
  const [buttonColor, setButtonColor] = useState('#fff');
  const [buttonTextColor, setButtonTextColor] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [headline, setHeadline] = useState(DefaultText.headline);
  const [successText, setSuccessText] = useState(
    DefaultText?.succesText.replaceAll('{OrgName}', user?.organization?.name),
  );
  const [integrationType, setIntegrationType] = useState(
    INTEGRATION_TYPES.AUTO_POP_UP,
  );
  const [introductoryText, setIntroductoryText] = useState(
    DefaultText.introductoryText,
  );
  const [embedScriptUrl, setEmbedScriptUrl] = useState('');
  const { loading: scriptUrlLoading } = useQuery(GET_ENV_DATA, {
    fetchPolicy: 'network-only',
    onCompleted(reponse) {
      setEmbedScriptUrl(reponse?.getEnvData?.embedScriptUrl);
    },
  });
  const [errors, setErrors] = useState({});

  const [updateIntegrationConf, { data, loading }] = useMutation(
    UPDATE_ORG_INTEGRATION,
    {
      onCompleted(data) {
        toast.success('Updated Successfully');
        setUser((oldUser) => ({
          ...oldUser,
          organization: {
            ...oldUser.organization,
            integration: data?.updateOrgIntegrationSetting?.integration,
          },
        }));
      },
      onError(error) {
        console.error(getErrorMessage(error));
        console.log(error);
      },
    },
  );
  const { data: orgData } = useQuery(GET_CURRENT_ORGANIZATION);
  const tagsOption = orgData?.getCurrentOrganization?.tags;
  useEffect(() => {
    if (orgData?.getCurrentOrganization?.integration?.tags) {
      const initialSelectedTags = orgData.getCurrentOrganization.integration.tags.map((tag) => ({
        value: tag,
        label: tag,
      }));
      setSelectedTags(initialSelectedTags);
    }
  }, [orgData]);
  const tagsOptions = tagsOption?.map((tag) => ({ value: tag, label: tag })) || [];
  const handleTagChange = (selectedOptions) => {
    setSelectedTags(selectedOptions || []);
  };
  const customStyles = {
    option: (provided) => ({
      ...provided,
      fontSize: '13px',
    }),
  };
  const HTML = getEmbedHTMLForButton(
    user?.organization?._id,
    'Referral Program Opt-in',
    buttonColor,
    buttonTextColor,
    backgroundColor,
    integrationType,
  );

  useEffect(() => {
    let { buttonColor, buttonTextColor, backgroundColor } = DefaultColors;
    let type = INTEGRATION_TYPES.AUTO_POP_UP;

    const integration = user?.organization?.integration;
    if (integration) {
      buttonColor = integration.buttonColor || buttonColor;
      buttonTextColor = integration.buttonTextColor || buttonTextColor;
      backgroundColor = integration.backgroundColor || backgroundColor;
      type = getNormalizedIntegrationType(integration.integrationType);
      setIntroductoryText(
        integration.introductoryText || DefaultText.introductoryText,
      );
      setHeadline(integration.headline || DefaultText.headline);
      setSuccessText(
        integration.successText
          || DefaultText?.succesText.replaceAll(
            '{OrgName}',
            user?.organization?.name,
          ),
      );
    }
    setIntegrationType(type);
    setButtonColor(buttonColor);
    setButtonTextColor(buttonTextColor);
    setBackgroundColor(backgroundColor);
  }, [user]);

  const hasFormErrors = () => {
    return errors?.headline || errors.introductoryText;
  };

  // Closing the popup window on route change
  useEffect(() => {
    return () => {
      window.parent.CTO.close();
    };
  }, []);

  const isButton = integrationType === INTEGRATION_TYPES.BUTTON;
  const isAutoPopUp = integrationType === INTEGRATION_TYPES.AUTO_POP_UP;

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        if (!hasFormErrors()) {
          const integration = {
            headline,
            buttonColor,
            buttonTextColor,
            backgroundColor,
            introductoryText,
            integrationType,
            successText,
            tags: selectedTags.map((tag) => tag.value),
          };
          updateIntegrationConf({ variables: { integration } });
        }
      }}
    >
      <Card width="100%" padding="20px 30px">
        <div data-help="integration-widget-style" style={{ width: '100%' }}>
          <InputContainer>
            <Input
              type="text"
              header="Headline*"
              required
              onChange={(e) => {
                if (e.target.value.length > TextLength.max.headline) {
                  setErrors((oldErrors) => ({
                    ...oldErrors,
                    headline: getMaxCharErrorMessage(TextLength.max.headline),
                  }));
                } else {
                  setErrors((oldErrors) => ({ ...oldErrors, headline: false }));
                }
                setHeadline(e.target.value);
              }}
              error={errors?.headline}
              value={headline}
              width="100%"
            />
          </InputContainer>
          <InputContainer>
            <Input
              type="color"
              header="Button Color"
              required
              onChange={(e) => setButtonColor(e.target.value)}
              value={buttonColor}
              width="97%"
            />
            <Input
              type="color"
              header="Button Text Color"
              required
              onChange={(e) => setButtonTextColor(e.target.value)}
              value={buttonTextColor}
              width="97%"
            />
            <Input
              type="color"
              header="Background Color"
              required
              onChange={(e) => setBackgroundColor(e.target.value)}
              value={backgroundColor}
              width="100%"
            />
          </InputContainer>
          <InputContainer>
            <TextAreaInput
              width="100%"
              type="text"
              header="Introductory Text *"
              name="text"
              onChange={(e) => {
                if (
                  e.target.value.trim().length > TextLength.max.introductoryText
                ) {
                  setErrors((oldErrors) => ({
                    ...oldErrors,
                    introductoryText: getMaxCharErrorMessage(
                      TextLength.max.introductoryText,
                    ),
                  }));
                } else {
                  setErrors((oldErrors) => ({
                    ...oldErrors,
                    introductoryText: false,
                  }));
                }
                setIntroductoryText(e.target.value);
              }}
              value={introductoryText}
              error={errors?.introductoryText}
              required
            />
          </InputContainer>
          <InputContainer>
            <TextAreaInput
              width="100%"
              type="text"
              header="Successful Enrollment Text*"
              name="success_message"
              onChange={(e) => {
                setSuccessText(e.target.value);
              }}
              value={successText}
              required
            />
          </InputContainer>

          <LabelDiv>
            {' '}
            <Label marginTop="30px" marginBottom="10px">
              Integration Type
            </Label>
            <div data-tip="You can select any of two types given below to integrate this functionality on your website.">
              <Icon
                size={10}
                margin="32px 0 10px 10px"
                icon={IconEnum.infoRegular}
              />
            </div>
          </LabelDiv>
          <InputContainer
            justify="space-around"
            border={`1px solid ${colors.grey5}`}
            padding="10px"
          >
            <RadioContainer>
              <Input
                width="25px"
                height="30px"
                type="radio"
                name="type"
                checked={isButton}
                onChange={(e) => setIntegrationType(e.target.value)}
                value={INTEGRATION_TYPES.BUTTON}
                required
              />
              <Label>Button</Label>
              <div data-tip="If you will select this option a button will displayed to open CTO website modal">
                <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
              </div>
            </RadioContainer>

            <RadioContainer>
              <Input
                width="25px"
                height="30px"
                type="radio"
                name="type"
                onChange={(e) => {
                  setIntegrationType(e.target.value);
                }}
                checked={isAutoPopUp}
                value={INTEGRATION_TYPES.AUTO_POP_UP}
                required
              />
              <Label>Auto Pop-up</Label>
              <div data-tip="If you will select this option CTO website modal will auto Pop-up">
                <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
              </div>
            </RadioContainer>
          </InputContainer>
        </div>

        {isButton && (
          <InputContainer
            justify="flex-start"
            direction="column"
            margin=" 0"
            border={`1px solid ${colors.grey5}`}
          >
            <div style={{ width: '200px', margin: '20px' }}>
              <div dangerouslySetInnerHTML={{ __html: HTML }} />
            </div>
            <Label marginTop="10px" marginBottom="10px">
              Click on the button above to what your modal will look like.
            </Label>
          </InputContainer>
        )}

        <div data-help="integration-embed-head-script" style={{ width: '100%' }}>
          <Label marginTop="30px" marginBottom="10px">
            Paste this code snippet into {'<head>'} of your entire site
          </Label>

          <InputContainer justify="space-around" margin=" 0">
            <ScriptDiv>
              {isButton ? (
                <>
                  {scriptUrlLoading ? (
                    <Text>Loading...</Text>
                  ) : (
                    <>
                      <Text>
                        {`<script src="${
                          embedScriptUrl || `${window.location.origin}/embed.js`
                        }"></script>`}
                      </Text>
                    </>
                  )}
                </>
              ) : (
                <div style={{ minHeight: '100px' }}>
                  <Text>
                    {`<script src="${
                      embedScriptUrl || `${window.location.origin}/embed.js`
                    }" defer onload="setTimeout(() => {
                        window.parent.CTO.open('${
                          user?.organization?._id
                        }', true)
                      }, 3000)"></script>`}
                  </Text>
                </div>
              )}
            </ScriptDiv>
          </InputContainer>

          {isButton && (
            <>
              <Label marginTop="30px" marginBottom="10px">
                Embed this code where you want the button to show up
              </Label>

              <InputContainer
                justify="space-around"
                margin=" 0"
                border={`1px solid ${colors.grey5}`}
              >
                <ScriptDiv>
                  <Text>{HTML}</Text>
                </ScriptDiv>
              </InputContainer>
            </>
          )}
        </div>
        {/* //TODO Addded dropdown to save Tags */}
        <DropdownHeader>Auto-apply Tag(s)</DropdownHeader>
        <DropdownContainer>
          <Select
            onChange={handleTagChange}
            options={tagsOptions}
            isClearable={false}
            isMulti
            value={selectedTags}
            styles={customStyles}
          />
        </DropdownContainer>
        <InputContainer
          // justify="space-around"
          margin="15px"
        >
          <Button
            text="Update Configurations"
            disabled={hasFormErrors()}
            loading={loading}
            width="max-content"
          />
        </InputContainer>
      </Card>
      <ReactTooltip place="right" type="dark" effect="solid" />
    </Form>
  );
};

export default IntegrationSettings;
